import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Block,
  CheckCircle,
  Visibility,
  TrendingUp,
  TrendingDown,
  Search,
  FilterList
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PRateManagement = ({ onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [disableReason, setDisableReason] = useState('');
  const [filters, setFilters] = useState({
    cryptocurrency: '',
    fiat_currency: '',
    trade_type: '',
    is_active: '',
    partner_id: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  useEffect(() => {
    fetchRates();
  }, [filters, pagination.current_page]);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const { default: apiClient } = await import('../../request/http-common');
      
      const params = new URLSearchParams({
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...filters
      });

      const response = await apiClient.get(`/api/admin/p2p/rates?${params}`);
      
      if (response.data && response.data.success && response.data.rates) {
        setRates(response.data.rates.data || []);
        setPagination({
          current_page: response.data.rates.current_page || 1,
          per_page: response.data.rates.per_page || 15,
          total: response.data.rates.total || 0,
          last_page: response.data.rates.last_page || 1
        });
      } else {
        setRates([]);
        setPagination({
          current_page: 1,
          per_page: 15,
          total: 0,
          last_page: 1
        });
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableRate = (rate) => {
    setSelectedRate(rate);
    setDisableReason('');
    setDisableDialogOpen(true);
  };

  const executeDisable = async () => {
    try {
      const { default: apiClient } = await import('../../request/http-common');
      const response = await apiClient.post(`/api/admin/p2p/rates/${selectedRate.id}/disable`, {
        reason: disableReason
      });
      
      if (response.data.success) {
        fetchRates();
        onUpdate && onUpdate();
        setDisableDialogOpen(false);
      }
    } catch (error) {
      console.error('Error disabling rate:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (event, page) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  const getTradeTypeColor = (type) => {
    return type === 'buy' ? 'success' : 'error';
  };

  const getTradeTypeIcon = (type) => {
    return type === 'buy' ? <TrendingUp /> : <TrendingDown />;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter Rates
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Crypto</InputLabel>
                <Select
                  value={filters.cryptocurrency}
                  onChange={(e) => handleFilterChange('cryptocurrency', e.target.value)}
                  label="Crypto"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="BTC">BTC</MenuItem>
                  <MenuItem value="ETH">ETH</MenuItem>
                  <MenuItem value="USDT">USDT</MenuItem>
                  <MenuItem value="USDC">USDC</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Fiat</InputLabel>
                <Select
                  value={filters.fiat_currency}
                  onChange={(e) => handleFilterChange('fiat_currency', e.target.value)}
                  label="Fiat"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="NGN">NGN</MenuItem>
                  <MenuItem value="GHS">GHS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.trade_type}
                  onChange={(e) => handleFilterChange('trade_type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="buy">Buy</MenuItem>
                  <MenuItem value="sell">Sell</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="0">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rates Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Partner</TableCell>
              <TableCell>Pair</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Limits</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map((rate) => (
              <TableRow key={rate.id}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {rate.partner?.partner_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {rate.partner?.user?.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${rate.cryptocurrency}/${rate.fiat_currency}`}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getTradeTypeIcon(rate.trade_type)}
                    label={rate.trade_type.toUpperCase()}
                    color={getTradeTypeColor(rate.trade_type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(rate.rate, rate.fiat_currency)}
                  </Typography>
                  {rate.margin_percentage && (
                    <Typography variant="caption" color="text.secondary">
                      {rate.margin_percentage > 0 ? '+' : ''}{rate.margin_percentage}%
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatCurrency(rate.min_amount, rate.fiat_currency)} - {formatCurrency(rate.max_amount, rate.fiat_currency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {rate.available_balance} {rate.cryptocurrency}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={rate.is_active ? 'Active' : 'Inactive'}
                    color={rate.is_active ? 'success' : 'default'}
                    size="small"
                  />
                  {rate.disabled_by && (
                    <Typography variant="caption" color="error.main" display="block">
                      Disabled by admin
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {rate.is_active && (
                      <Tooltip title="Disable Rate">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDisableRate(rate)}
                        >
                          <Block />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.last_page}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Disable Rate Dialog */}
      <Dialog
        open={disableDialogOpen}
        onClose={() => setDisableDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disable Rate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You are about to disable the rate for {selectedRate?.cryptocurrency}/{selectedRate?.fiat_currency} 
            by {selectedRate?.partner?.partner_name}.
          </Typography>
          <TextField
            fullWidth
            label="Reason for disabling"
            multiline
            rows={3}
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={executeDisable}
            color="error"
            variant="contained"
            disabled={!disableReason}
          >
            Disable Rate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default P2PRateManagement;
